import { ComponentType, FC, Ref, SVGProps, forwardRef, memo } from 'react';

import { SvgIcon, SvgIconProps } from '@mui/material';
import { isProduction } from 'common/utils';

import { useInheritedVisualRefresh } from './useInheritedVisualRefresh';

import type { Icon as PhosphorIcon, IconWeight } from '@phosphor-icons/react';

/**
 * The Phosphor counterpart of a legacy glyph (HNS-262), or `null` for "no genuine 1:1 match".
 *
 * `null` is not a gap in the work — it is the answer for every glyph Phosphor cannot express, and it
 * means "keep today's SVG whatever the flag says". Refusing beats approximating, for the reason
 * `CONTRIBUTING.md` gives for the button mappers: a near-miss ships an icon that is subtly wrong with
 * nothing to catch it at review, whereas a `null` preserves today's appearance and leaves a visible
 * list of design questions. `icons/phosphorMapping.ts` is that list.
 *
 * TODO(HNS-264): retire this alongside the flag — the matched glyphs become plain Phosphor imports
 * and the unmatched ones become whatever design decides.
 */
export type PhosphorSwap = { icon: PhosphorIcon; weight?: IconWeight } | null;

/**
 * Render a Phosphor glyph through MUI's `SvgIcon`, so it keeps the `SvgIconProps` contract every
 * call site already uses — `fontSize`, `color`, `sx`.
 *
 * Two things here are load-bearing and look like omissions:
 *
 *   - **No `sx={{ fill }}`, ever.** Phosphor's `<path>` elements carry no `fill` attribute and
 *     inherit it from the `<svg>` root, which `IconBase` sets to `currentColor` as an ATTRIBUTE. A
 *     CSS rule beats a presentational attribute, so the legacy `sx={{ fill: 'transparent' }}` would
 *     render the icon invisible — laid out, sized, focusable, and completely blank. `SvgIconRoot`
 *     already emits `fill: currentColor` for us, which is exactly what Phosphor wants.
 *   - **`weight` is passed straight through.** It is not in `SvgIcon`'s excluded-prop list, and
 *     `SvgIconRoot` is a `slot: 'Root'` styled component, so it forwards unknown props via
 *     `rootShouldForwardProp` rather than filtering them through Emotion's `isPropValid`. That lets
 *     us keep a stable component identity — wrapping the Phosphor component to bind `weight` would
 *     remount the icon on every render.
 *
 * `inheritViewBox` so Phosphor's own `0 0 256 256` survives; `IconBase` declares it before spreading
 * props, so passing one would silently win.
 *
 * A plain function rather than a component, because the two factories need to hand it the `ref` they
 * were given: `ref` on a function component is consumed by React itself and never reaches props.
 */
const renderPhosphorIcon = (
  phosphor: NonNullable<PhosphorSwap>,
  testId: string,
  ref: Ref<SVGSVGElement>,
  props: SvgIconProps,
  defaults: Pick<SvgIconProps, 'fontSize'> = {}
) => (
  <SvgIcon
    component={phosphor.icon}
    weight={phosphor.weight ?? 'regular'}
    data-testid={testId}
    ref={ref}
    inheritViewBox
    {...defaults}
    {...props}
  />
);

/**
 * Private module reserved for @mui packages.
 * Based on https://github.com/mui/material-ui/blob/master/packages/mui-material/src/utils/createSvgIcon.js
 *
 * `phosphor` swaps the glyph when the Design System 2.0 refresh is on (HNS-262). Branching here
 * rather than at the call sites is what keeps the swap free: this one factory backs ~90 exports in
 * `icons/index.ts` plus five feature-local barrels, and none of the 200-odd files importing them
 * mentions the flag. It is the same "branch inside the wrapper" rule `CONTRIBUTING.md` sets out for
 * components.
 *
 * The flag is read through the theme rather than the constant so Storybook's toolbar can flip it —
 * see `useInheritedVisualRefresh`.
 */
export const createSvgIcon = (
  component: FC<SVGProps<SVGSVGElement>>,
  displayName: string,
  phosphor: PhosphorSwap = null
) => {
  const Component = <T,>(props: SvgIconProps, ref: Ref<T>) => {
    const visualRefresh = useInheritedVisualRefresh();

    if (visualRefresh && phosphor) {
      return renderPhosphorIcon(phosphor, `${displayName}Icon`, ref as Ref<SVGSVGElement>, props, {
        fontSize: 'inherit',
      });
    }

    // `sx` is unconditional on purpose. Making it a ternary changes the serialised style object and
    // so rotates the Emotion class hash, which `IconButton.test.tsx.snap` pins — a snapshot failure
    // with the flag OFF, where nothing should have moved.
    return (
      <SvgIcon
        component={component}
        data-testid={`${displayName}Icon`}
        ref={ref}
        inheritViewBox
        sx={{ fill: 'transparent' }}
        fontSize="inherit"
        {...props}
      />
    );
  };

  if (!isProduction()) {
    // Need to set `displayName` on the inner component for React.memo.
    // React prior to 16.14 ignores `displayName` on the wrapper.
    Component.displayName = `${displayName}Icon`;
  }

  Component.muiName = SvgIcon.muiName;

  return memo(forwardRef(Component));
};

/**
 * The same swap for a glyph that comes from `@mui/icons-material` rather than a local SVG — the other
 * half of `icons/index.ts` (HNS-262).
 *
 * Three differences from `createSvgIcon`, each of which breaks something if dropped:
 *
 *   - **No `fontSize="inherit"`.** A Material re-export has always rendered at MUI's `medium` (24px)
 *     default; only the local-SVG factory pins `inherit`. Adding it here would shrink every Material
 *     icon in the app to its parent's font size, with the flag off.
 *   - **`testId` is a separate argument** from `displayName`, because our export name and MUI's
 *     `data-testid` disagree for five icons — `CellTowerIcon` renders `CellTowerRoundedIcon`,
 *     `GroupsFilledIcon` renders `GroupsIcon`, and so on. Unit tests and the Playwright suite query
 *     those strings, so the refreshed branch has to emit MUI's, not ours.
 *   - **The legacy branch adds nothing at all**, not even a `data-testid`: MUI's own factory already
 *     sets one, and restating it here would be a second source of truth for the same string.
 */
export const createMaterialIcon = (
  Material: ComponentType<SvgIconProps>,
  testId: string,
  displayName: string,
  phosphor: PhosphorSwap = null
) => {
  const Component = <T,>(props: SvgIconProps, ref: Ref<T>) => {
    const visualRefresh = useInheritedVisualRefresh();

    if (visualRefresh && phosphor) {
      return renderPhosphorIcon(phosphor, `${testId}Icon`, ref as Ref<SVGSVGElement>, props);
    }

    return <Material ref={ref as Ref<SVGSVGElement>} {...props} />;
  };

  if (!isProduction()) {
    Component.displayName = `${displayName}Icon`;
  }

  Component.muiName = SvgIcon.muiName;

  return memo(forwardRef(Component));
};
